'use client';

import { useCallback, useEffect, useState } from 'react';

import { useAiToolkitContext } from '../../../../_shared/_context/AiToolkitContext';
import { VIDEO_EDIT_FORM_DEFAULTS } from '../../../../_shared/_context/VideoEditFormState';
import { AI_TOOLKIT_CATEGORIES } from '../../../../_shared/_context/types';
import { DEFAULT_SELECTION_DURATION, type VideoSelection } from '../../FilmStripSlider';

/**
 * Dragging fires a change per pointer move. The form value is only read after submit, so it is
 * written on a trailing edge to keep every drag frame from re-rendering the whole toolkit provider.
 */
const FORM_SYNC_DEBOUNCE_MS = 150;

interface UseVideoEditSelectionArgs {
    /** Resets the window when a different source video is chosen. */
    videoSrc: string;
    videoDuration: number;
    /** Used on first mount only; when absent, defaults to a window starting at 0. */
    initialSelection?: VideoSelection;
}

interface UseVideoEditSelectionReturn {
    /** Null until the source duration is known and a window can be sized. */
    selection: VideoSelection | null;
    onSelectionChange: (selection: VideoSelection) => void;
}

function buildInitialSelection(videoDuration: number): VideoSelection {
    const duration = Math.min(videoDuration, DEFAULT_SELECTION_DURATION);

    return { startTime: 0, endTime: duration, duration };
}

export function useVideoEditSelection({
    videoSrc,
    videoDuration,
    initialSelection,
}: UseVideoEditSelectionArgs): UseVideoEditSelectionReturn {
    const { formStates, updateFormState } = useAiToolkitContext();
    const [selection, setSelection] = useState<VideoSelection | null>(null);

    // initialSelection is intentionally excluded — it only seeds the first mount.
    useEffect(() => {
        if (!videoSrc || videoDuration <= 0) {
            setSelection(null);
            return;
        }

        setSelection(initialSelection ?? buildInitialSelection(videoDuration));
    }, [videoSrc, videoDuration]);

    const startTime = selection?.startTime;
    const duration = selection?.duration;

    // Both edges matter: trimming the right handle changes only the duration.
    useEffect(() => {
        if (startTime === undefined || duration === undefined) return;

        const timer = setTimeout(() => {
            const formState = formStates[AI_TOOLKIT_CATEGORIES.VIDEO_EDIT] ?? VIDEO_EDIT_FORM_DEFAULTS;
            if (formState.startTime === startTime && formState.duration === duration) return;

            updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, { ...formState, startTime, duration });
        }, FORM_SYNC_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [startTime, duration, formStates, updateFormState]);

    const onSelectionChange = useCallback((next: VideoSelection): void => {
        setSelection(next);
    }, []);

    return { selection, onSelectionChange };
}
